import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import PropTypes from "prop-types";
import React from "react";
import cx from "classnames";
import createUrl from "../../utilities/createUrl/index.js";
import createTransformationQuery from "../../utilities/createTransformationQuery/index.js";
import { useImagePreloadRenderer } from "../ImagePreloadExtractor/index.js";
import intrinsicDimensionsFromProps from "../../utilities/intrinsicDimensionsFromProps/index.js";
import transformationsShape from "../transformationsShape.js";
/**
 * Render an img element. Can be used by itself or as a child to `Picture`
 */

const Img = props => {
  const src = createUrl(props);
  const {
    lazy,
    alt,
    preload,
    renderPreload: givenRenderPreload,
    decoding,
    className,
    elementProps
  } = props;
  const {
    width,
    height
  } = intrinsicDimensionsFromProps(props);
  const srcProp = lazy ? 'data-src' : 'src';
  const lazyClassName = lazy ? 'lazyload' : '';
  const fullSrc = `${src}${createTransformationQuery(props)}`;
  const imgProps = {
    [srcProp]: fullSrc
  };
  const classes = cx(lazyClassName, className);
  const renderPreload = useImagePreloadRenderer(givenRenderPreload);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("img", _extends({}, elementProps, imgProps, {
    width: width,
    height: height,
    className: classes,
    alt: alt,
    decoding: decoding
  })), lazy && /*#__PURE__*/React.createElement("noscript", null, /*#__PURE__*/React.createElement("img", {
    alt: alt,
    src: fullSrc,
    width: width,
    height: height
  })), preload && renderPreload({
    rel: 'preload',
    as: 'image',
    href: fullSrc
  }));
};

Img.defaultProps = {
  className: '',
  lazy: false,
  elementProps: {},
  alt: '',
  decoding: 'async'
};
process.env.NODE_ENV !== "production" ? Img.propTypes = {
  /**
   * Preload image. Requires setting up a way to extract preloads. Is only allowed on this element when used without the parent `Picture` component
   */
  preload: PropTypes.bool,

  /**
   * Override how preloads are rendered
   */
  renderPreload: PropTypes.func,

  /**
   * Enable lazy loading
   */
  lazy: PropTypes.bool,

  /**
   * Pass additional props to underlying element
   */
  elementProps: PropTypes.shape({}),

  /**
   * Extend class name
   */
  className: PropTypes.string,

  /**
   * Necessary for accessibility
   */
  alt: PropTypes.string,

  /**
   * Decoding strategy. Async decoding improves render performance though it causes images to be gradually filled in instead of painted all at once.
   */
  decoding: PropTypes.oneOf(['async', 'auto', 'sync']),

  /**
   * Set the desired displayed width of the image
   */
  width: PropTypes.number,

  /**
   * Set the desired displayed height of the image
   */
  height: PropTypes.number,

  /**
   * A 4 element tuple with the crop coordinates (`[x1, y1, x2, y2]`)
   * to pass to the media API. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  crop: PropTypes.arrayOf(PropTypes.number),

  /**
   * Enable smartCrop as the sizing strategy. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  smartCrop: PropTypes.bool,

  /**
   * Force image to fit in given dimensions (Only makes sense when height and width are provided).
   *
   * [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  fit: PropTypes.bool,

  /**
   * Number of degrees to rotate the image
   *
   * [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  rotate: PropTypes.number,

  /**
   * Add a slug to the image url. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  slug: PropTypes.string,

  /**
   * Override transformations. If array is provided, transformations are applied in the given order. Transformations shape includes
   *
   * | name | type |
   * |------|------|
   * | width | `number` |
   * | height | `number` |
   * | rotate | `number` |
   * | crop | `[x1: number, y1: number, x2: number, y2: number]` |
   * | fit | `bool` |
   * | smartCrop | `bool` |
   */
  transformations: PropTypes.oneOfType([transformationsShape, PropTypes.arrayOf(transformationsShape)])
} : void 0; // eslint-disable-next-line no-underscore-dangle

Img.__isImgNode = true;
export default Img;